/**
 * The masthead: the dark surface that carries the navigation and the hero as
 * one continuous piece of the page.
 *
 * ── WHERE THE GRADIENT COMES FROM ─────────────────────────────────────────
 * It is the hero P3 already ships in two places, carried over verbatim:
 *   · `p3-web/src/features/admin/admin.route.tsx`, the signed-in dashboard;
 *   · `p3-data-matrix/src/render.ts`, the Matrix.
 * A 112 degree maroon gradient, a crimson radial bloom at 80% 28%, Bricolage
 * Grotesque on the heading with exactly one crimson accent word, and a stat
 * row separated by hairline rules rather than boxed into tiles.
 *
 * ── WHAT CHANGED IN THIS PASS ─────────────────────────────────────────────
 * It used to be a rounded card floating on cream, and Thomas read it exactly
 * as what it was: "a hero section we brought in from outside", an island with
 * a white nav above it and a white body below it. It is now full bleed and it
 * reaches UP past the header, so the nav sits inside the same dark surface and
 * there is no seam to see. That is the highest-leverage change on the page: it
 * kills the largest white expanse, it puts the white P3 lockup on a ground it
 * was drawn for, and it is the masthead a visitor coming from
 * www.pulseofp3.org has just left.
 *
 * ONE ELEMENT paints the whole surface (`.eg-mast-bg`), rather than the header
 * and the hero each painting a matched half. Two matched gradients on two
 * boxes register correctly only while both boxes keep the height you assumed;
 * one element cannot mis-register at all.
 *
 * ── THE FOUR DEPTH LAYERS, AND WHY EACH ONE IS THERE ──────────────────────
 * Thomas: "No watermarks, photos, branding elements." A flat rectangle of
 * gradient is still a wireframe. Inside `.eg-mast-bg`, in paint order:
 *
 *   1. campus-bg.jpg, the photograph p3-web runs behind the whole member app,
 *      at 0.34 in soft-light, positioned center 22%, and holding full strength
 *      to 30% before it fades. It reads as architecture rather than as a wash.
 *      Raised from 0.14 on Sep 8 2026 when the P3 mark came out from beside
 *      it: two watermarks on one surface were competing, and Thomas wanted the
 *      building, not the logo.
 *
 *      BEFORE RAISING IT FURTHER, re-measure. The binding constraint is the
 *      crimson accent word in the H1, which sits at 4.41:1 at 1440 and 4.55:1
 *      at 390 against a 3:1 floor for large text. Every white figure has ten
 *      points in hand; the accent does not.
 *   2. the crimson radial bloom, the P3 signature, unchanged.
 *   3. a fractal-noise grain at 0.2 in overlay. A 900px tall two-stop maroon
 *      gradient bands visibly on any 8-bit panel, and grain is the standard
 *      fix: it dithers the steps below the eye's threshold. This is the layer
 *      nobody notices and everybody would notice the absence of.
 *
 * Then a soft inner vignette on `::after`, which darkens the outer edges so
 * the surface reads as lit rather than printed.
 *
 * Legibility is not traded for any of it: the copy sits above every layer, on
 * the part of the gradient the photo mask has already faded out of.
 *
 * ── HOW IT ENDS ───────────────────────────────────────────────────────────
 * Not on a hard rectangle edge. The surface's bottom is a single very shallow
 * arc (`border-radius` with a 50% horizontal and a 40px vertical radius) with
 * a soft shadow under it, so the dark lifts off the light body instead of
 * being cut against it.
 *
 * ── THE STATS ARE OPTIONAL, AND THE GATEWAY NO LONGER PASSES ANY ──────────
 * /pricing dropped its four stat cells on September 8 2026 so the capacity
 * slider is the first instrument a buyer meets. /trust still renders three,
 * so the prop and `.eg-kpis` stay. The rule they are held to is unchanged:
 * every cell must resolve to something P3 can stand behind in a security
 * review, and there is deliberately no user count, no growth figure and no
 * customer logo, because P3 has no paying institutional partner yet and a
 * hero that implied one would be the exact failure this surface was built to
 * avoid.
 */

import type { ReactNode } from "react";

import { cn } from "~/app/lib/cn";

export interface HeroStat {
  /** 11px uppercase. One to three words. */
  readonly label: string;
  /** The figure or the word. 30px Bricolage. */
  readonly value: ReactNode;
  /** 12px. Three to five words, and it is not a delta on this surface. */
  readonly detail: string;
}

export interface GatewayHeroProps {
  eyebrow?: string;
  /** Wrap ONE word or phrase in <HeroAccent> and no more. */
  title: ReactNode;
  /** At most 25 words. The rebrief sets that ceiling and it is a ceiling. */
  sub: string;
  stats?: readonly HeroStat[];
  /** Rendered under the stat row, inside the surface. */
  footer?: ReactNode;
  className?: string;
}

/** The single crimson word in the heading. Crimson at 40px clears 3:1, which
 *  is the graphics and large-text threshold; it is not used below 24px. */
export function HeroAccent({ children }: { children: ReactNode }) {
  return <span className="eg-accent">{children}</span>;
}

/**
 * The painted surface. Absolutely positioned, full bleed, and lifted above its
 * own box by `--mast-lift` so it runs behind the header. Rendered as a sibling
 * of the copy rather than as a background on `.eg-band`, because a background
 * cannot bleed past its own box and four stacked layers cannot live on one.
 */
function MastheadSurface() {
  return (
    <div className="eg-mast-bg" aria-hidden="true">
      <span className="eg-mast-photo" />
      <span className="eg-mast-bloom" />
      <span className="eg-mast-grain" />
    </div>
  );
}

export function GatewayHero({
  eyebrow,
  title,
  sub,
  stats,
  footer,
  className,
}: GatewayHeroProps) {
  return (
    <section className={cn("eg-band", className)}>
      <MastheadSurface />

      {eyebrow ? <p className="eg-band-eyebrow">{eyebrow}</p> : null}
      <h1>{title}</h1>
      <p className="eg-band-sub">{sub}</p>

      {stats && stats.length > 0 ? (
        <div className="eg-kpis">
          {stats.map((stat) => (
            <div className="eg-kpi" key={stat.label}>
              <div className="eg-kpi-l">{stat.label}</div>
              <div className="eg-kpi-v">{stat.value}</div>
              <div className="eg-kpi-d">{stat.detail}</div>
            </div>
          ))}
        </div>
      ) : null}

      {footer}
    </section>
  );
}
